import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Grid,
  Alert,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { getExpenses, createExpense, deleteExpense } from "../../api/expenses";
import TripSelect from "../../components/TripSelect";
import { useTranslation } from "react-i18next";

const EXPENSE_TYPE_KEYS = ["fuel", "food", "toll", "parking", "other"];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const DriverExpensesPage = () => {
  const { t } = useTranslation("common");
  const [trip, setTrip] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE_OPTIONS[0]);

  const [newExpense, setNewExpense] = useState({
    expense_type: "fuel",
    amount: "",
    comment: "",
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (!trip) return;
    const fetchData = async () => {
      const res = await getExpenses(page + 1, rowsPerPage, trip.id);
      setExpenses(res.data.results || []);
      setTotalCount(res.data.count || 0);
    };
    fetchData();
  }, [trip, page, rowsPerPage]);

  const validateForm = () => {
    const err = {};
    const amount = Number(newExpense.amount);
    if (Number.isNaN(amount) || amount < 0) {
      err.amount = t("driverExpenseForm.amountMin");
    }
    return Object.keys(err).length ? err : null;
  };

  const handleAdd = async () => {
    setFormErrors({});
    const errors = validateForm();
    if (errors) {
      setFormErrors(errors);
      return;
    }
    try {
      await createExpense({
        trip: trip.id,
        expense_type: newExpense.expense_type,
        amount: Number(newExpense.amount),
        comment: newExpense.comment || "",
      });
      setNewExpense({ expense_type: "fuel", amount: "", comment: "" });
      const res = await getExpenses(page + 1, rowsPerPage, trip.id);
      setExpenses(res.data.results || []);
      setTotalCount(res.data.count || 0);
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 400 && data) {
        const flat = typeof data === "object" ? data : { _form: String(data) };
        setFormErrors(flat);
      } else {
        setFormErrors({
          _form: data?.detail || t("driverExpenseForm.addError"),
        });
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteExpense(id);
      const res = await getExpenses(page + 1, rowsPerPage, trip.id);
      setExpenses(res.data.results || []);
      setTotalCount(res.data.count || 0);
    } catch (e) {
      console.error("Delete expense error", e);
    }
  };

  return (
    <Box p={2} sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        {t("driverExpenseForm.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("driverExpenseForm.hint")}
      </Typography>
      <TripSelect
        value={trip}
        onChange={setTrip}
        dateFrom={new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)}
      />

      {trip && (
        <Box sx={{ mt: 2, mb: 2 }}>
          {formErrors._form && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() =>
                setFormErrors((p) => ({ ...p, _form: undefined }))
              }
            >
              {formErrors._form}
            </Alert>
          )}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t("driverExpenseForm.add")}
            </Typography>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("admin.expenseType")}
                  value={newExpense.expense_type}
                  onChange={(e) =>
                    setNewExpense((prev) => ({
                      ...prev,
                      expense_type: e.target.value,
                    }))
                  }
                >
                  {EXPENSE_TYPE_KEYS.map((value) => (
                    <MenuItem key={value} value={value}>
                      {t(`expenseType.${value}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  label={t("admin.amountEur")}
                  value={newExpense.amount}
                  onChange={(e) =>
                    setNewExpense((prev) => ({
                      ...prev,
                      amount: e.target.value,
                    }))
                  }
                  error={Boolean(formErrors.amount)}
                  helperText={formErrors.amount}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("admin.comment")}
                  value={newExpense.comment}
                  onChange={(e) =>
                    setNewExpense((prev) => ({
                      ...prev,
                      comment: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  variant="contained"
                  onClick={handleAdd}
                  fullWidth
                  sx={{ mt: 0.5 }}
                >
                  {t("admin.add")}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      )}

      {!trip ? (
        <Typography variant="body1" mt={2}>
          ⚠️ {t("admin.selectTripFirst")}
        </Typography>
      ) : (
        <Paper sx={{ mt: 3, overflowX: "auto" }}>
          <Table sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t("admin.addedBy")}</TableCell>
                <TableCell>{t("admin.expenseType")}</TableCell>
                <TableCell align="right">{t("admin.amountEur")}</TableCell>
                <TableCell>{t("admin.comment")}</TableCell>
                <TableCell>{t("admin.date")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {expenses.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.created_by_display || "—"}</TableCell>
                  <TableCell>
                    {row.expense_type_display || row.expense_type}
                  </TableCell>
                  <TableCell align="right">{row.amount}</TableCell>
                  <TableCell>{row.comment || "—"}</TableCell>
                  <TableCell>
                    {row.created_at
                      ? new Date(row.created_at).toLocaleString("uk-UA")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(row.id)}
                      aria-label={t("admin.delete")}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={2} align="right" sx={{ fontWeight: 600 }}>
                  {t("admin.pageSubtotal")}:
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {expenses
                    .reduce((sum, row) => sum + (Number(row.amount) || 0), 0)
                    .toFixed(2)}{" "}
                  €
                </TableCell>
                <TableCell colSpan={3} />
              </TableRow>
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            labelRowsPerPage={t("driverExpenseForm.rowsPerPage")}
          />
        </Paper>
      )}
    </Box>
  );
};

export default DriverExpensesPage;
